import { useEffect, useState } from "react";
import { useCurrency } from "@/context/CurrencyContext";
import { formatCurrency } from "@/lib/format";
import { ArrowLeftRight, Info } from "lucide-react";

/**
 * ConsolidatedRailCard — sums per-currency rails into the user's primary currency
 * via live FX (Frankfurter). Renders only when:
 *   • the FX-convert toggle is ON in the sidebar, AND
 *   • there are 2+ rails to consolidate.
 *
 * Props:
 *   rails: array of { currency, ...amounts }
 *   fields: array of { key, label, sign? }  — which numeric field(s) to sum
 *   testId: optional root data-testid
 */
export default function ConsolidatedRailCard({ rails, fields, testId = "consolidated-card", subtitle }) {
  const c = useCurrency() || {};
  const { convertOn, convert, primary, ensureRates, format } = c;
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!convertOn || !rails || rails.length < 2 || !ensureRates) return;
    let cancelled = false;
    (async () => {
      // Preload every rail's FX so conversion is instant.
      const ccys = [...new Set(rails.map((r) => r.currency).filter(Boolean))];
      await Promise.all(ccys.map((ccy) => ensureRates(ccy).catch(() => {})));
      if (!cancelled) setReady(true);
    })();
    return () => { cancelled = true; };
  }, [convertOn, rails, ensureRates]);

  if (!convertOn) return null;
  if (!Array.isArray(rails) || rails.length < 2) return null;

  const compute = (fieldKey) => {
    let total = 0;
    let missing = false;
    for (const r of rails) {
      const raw = Number(r[fieldKey] ?? 0);
      if (r.currency === primary) { total += raw; continue; }
      const c = convert(raw, r.currency, primary);
      if (c.currency !== primary) { missing = true; continue; }
      total += Number(c.amount);
    }
    return { total, missing };
  };

  const cells = fields.map((f) => ({ ...f, ...compute(f.key) }));

  return (
    <div data-testid={testId} className="card-flat p-4 border-l-4 border-l-[#2F4F4F]">
      <div className="flex items-center gap-2 mb-3">
        <ArrowLeftRight className="w-3.5 h-3.5 text-[var(--muted-text)]" />
        <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">
          Consolidated in {primary} · live FX
        </div>
        {!ready && (
          <span className="text-[10px] text-[var(--muted-text)] flex items-center gap-1">
            <Info className="w-3 h-3" /> fetching rates…
          </span>
        )}
        {subtitle && (
          <span className="text-[10px] normal-case text-[var(--muted-text)]">· {subtitle}</span>
        )}
      </div>
      {/* Static grid class map — Tailwind JIT purges dynamic `grid-cols-${n}`
          classes, so we look up an explicit safelist. Iter 60L. */}
      <div className={
        cells.length >= 4 ? "grid grid-cols-1 md:grid-cols-4 gap-4"
        : cells.length === 3 ? "grid grid-cols-1 md:grid-cols-3 gap-4"
        : cells.length === 2 ? "grid grid-cols-1 md:grid-cols-2 gap-4"
        : "grid grid-cols-1 gap-4"
      }>
        {cells.map((cell) => (
          <div key={cell.key} data-testid={`${testId}-${cell.key}`}>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">{cell.label}</div>
            <div className={`font-mono-data text-xl mt-1 ${cell.color || ""}`}>
              {(format ? format(cell.total, primary) : formatCurrency(cell.total, primary))}
              {cell.missing && <span className="ml-1 text-[10px] text-[var(--muted-text)]">*</span>}
            </div>
          </div>
        ))}
      </div>
      {cells.some((c) => c.missing) && (
        <div className="mt-2 text-[10px] text-[var(--muted-text)]">
          * one or more rails couldn&apos;t be converted (rate unavailable) and are shown at native value.
        </div>
      )}
    </div>
  );
}
